import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import seedrandom from 'seedrandom';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import logError from '@alltrails/core/utils/logError';
import { post } from '@alltrails/core/utils/post';
import type ExploreServiceFilters from '@alltrails/explore-service/types/ExploreServiceFilters';
import type ExploreServiceLocation from '@alltrails/explore-service/types/ExploreServiceLocation';
import type ExploreServicePayload from '@alltrails/explore-service/types/ExploreServicePayload';
import type ExploreServiceRecordIds from '@alltrails/explore-service/types/ExploreServiceRecordIds';
import type ExploreServiceResponse from '@alltrails/explore-service/types/ExploreServiceResponse';
import type ExploreServiceSort from '@alltrails/explore-service/types/ExploreServiceSort';
import { validExploreServiceAttributes } from '@alltrails/explore-service/utils/constants';
import CARD_ATTRIBUTES from '@alltrails/filters/constants/cardAttributes';
import type { LegacyFilters } from '@alltrails/filters/types/LegacyFilters';
import { convertLegacyFiltersToExploreService } from '@alltrails/filters/utils/convertLegacyFiltersToExploreService';
import { createExploreUrlFromFilters } from '@alltrails/filters/utils/createExploreUrlFromFilters';
import { ListsState } from '@alltrails/lists/slices/lists';
import { completedListId, getItemsInListOfType, verifiedListId } from '@alltrails/lists/utils/listUtils';
import { TrailSearchResult } from '@alltrails/search/types/algoliaResultTypes';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { countryIsoToSlug } from '../utils/countryIsoToSlug';
import type { HomepageSection, LocationData, TrailListSection } from '../utils/types';

const TRAIL_LIST = 'trail_list';
const MAX_RADIUS_MULTIPLIER = 3;
const TRAILS_PER_RIVER = 7;
const RANDOMNESS_FACTOR = 20; // more random = more shuffling, but potentially less ideal top results
const DEFAULT_AROUND_RADIUS = 48270; // 30 miles in meters
const EXTENDED_SEARCH_RADIUS = 144810; // 90 miles in meters (used for center+radius fallback)

type SearchStrategy = 'bbox' | 'center-radius' | 'country-slug';

// Filter CARD_ATTRIBUTES to only include valid explore-service attributes
const VALID_CARD_ATTRIBUTES = CARD_ATTRIBUTES.filter(attr => validExploreServiceAttributes.has(attr));

type UseSectionsParams = {
  initialSections: HomepageSection[];
  latLng?: [number, number];
  locationData?: LocationData;
};

/**
 * Convert geo object from server format to explore-service location format
 * Server format: { around_radius, around_precision, around_lat_lng }
 * Explore-service format: { center: { lat, lng }, maxRadius } or bounding box or slug
 */
function convertGeoToLocation(
  geo: LegacyFilters['geo'],
  defaultLat: number,
  defaultLng: number,
  radiusMultiplier: number = 1,
  locationData?: LocationData,
  searchStrategy: SearchStrategy = 'bbox'
): ExploreServiceLocation {
  // fallback 3 - use country slug
  if (searchStrategy === 'country-slug' && locationData?.country && !locationData?.city) {
    const countrySlug = countryIsoToSlug(locationData.country);
    if (countrySlug) {
      return { slug: countrySlug };
    }
  }

  // Parse lat/lng from geo or use defaults
  let lat = defaultLat;
  let lng = defaultLng;

  if (geo?.around_lat_lng) {
    const [latStr, lngStr] = geo.around_lat_lng.split(',');
    const parsedLat = parseFloat(latStr);
    const parsedLng = parseFloat(lngStr);
    if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
      lat = parsedLat;
      lng = parsedLng;
    }
  }

  // fallback 2 - center + maxRadius
  if (searchStrategy === 'center-radius') {
    return {
      center: { lat, lng },
      maxRadius: EXTENDED_SEARCH_RADIUS
    };
  }

  // Default 1 - bounding box with radius multiplier
  // Determine radius from around_radius or around_precision
  let radius = DEFAULT_AROUND_RADIUS;
  if (geo?.around_radius) {
    radius = Number(geo.around_radius);
  } else if (geo?.around_precision?.length) {
    // Use the maximum value from around_precision array
    const maxPrecision = Math.max(...geo.around_precision.map(p => Number(p.value)));
    radius = maxPrecision;
  }

  // Apply radius multiplier
  radius = radius * radiusMultiplier;

  // Convert radius to degrees (rough conversion: 1 degree ≈ 111km)
  const radiusInDegrees = radius / 111000;

  // Create bounding box from center point and radius
  // Ensure all values are numbers to prevent string concatenation
  const numLat = Number(lat);
  const numLng = Number(lng);
  const numRadiusInDegrees = Number(radiusInDegrees);

  return {
    bottomLeft: { lat: numLat - numRadiusInDegrees, lng: numLng - numRadiusInDegrees },
    bottomRight: { lat: numLat - numRadiusInDegrees, lng: numLng + numRadiusInDegrees },
    topLeft: { lat: numLat + numRadiusInDegrees, lng: numLng - numRadiusInDegrees },
    topRight: { lat: numLat + numRadiusInDegrees, lng: numLng + numRadiusInDegrees },
    mapRotation: 0
  };
}

/**
 * Create explore-service payload from converted filters and location
 */
function createExploreServicePayload(
  filters: ExploreServiceFilters,
  location: ExploreServiceLocation,
  sort: string | undefined,
  limit: number | undefined,
  idx: number,
  excludedTrailIds: number[]
): ExploreServicePayload {
  // Clean up filters - remove empty arrays and undefined values
  const cleanedFilters: Record<string, any> = {};
  let hasValidFilters = false;

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value) && value.length > 0) {
        cleanedFilters[key] = value;
        hasValidFilters = true;
      } else if (!Array.isArray(value)) {
        cleanedFilters[key] = value;
        hasValidFilters = true;
      }
    }
  });

  // Add recordIds to exclude completed and verified completed trails
  if (excludedTrailIds.length > 0) {
    const recordIds: ExploreServiceRecordIds[] = [
      {
        ids: excludedTrailIds,
        mode: 'not',
        recordType: 'trail'
      }
    ];
    cleanedFilters.recordIds = recordIds;
    hasValidFilters = true;
  }

  // Calculate default limit if not provided, or ensure minimum limit
  const defaultLimit = TRAILS_PER_RIVER * (idx + 1) + Math.ceil(TRAILS_PER_RIVER / 2);
  const finalLimit = limit && limit >= TRAILS_PER_RIVER ? limit : defaultLimit;

  // Validate sort value and default to 'most_popular' if invalid or undefined
  const validSorts: ExploreServiceSort[] = ['best_match', 'most_popular', 'closest', 'newly_added'];
  const validatedSort: ExploreServiceSort = sort && validSorts.includes(sort as ExploreServiceSort) ? (sort as ExploreServiceSort) : 'most_popular';

  const payload: ExploreServicePayload = {
    location,
    limit: finalLimit,
    recordTypesToReturn: ['trail'],
    recordAttributesToRetrieve: VALID_CARD_ATTRIBUTES,
    resultsToInclude: ['searchResults'],
    sort: validatedSort
  };

  if (hasValidFilters) {
    payload.filters = cleanedFilters as ExploreServiceFilters;
  }

  return payload;
}

/**
 * Create URL from explore-service filters for "explore more" link
 * Uses the shared utility from explore-service package
 */
function createUrlFromFilters(filters: ExploreServiceFilters, languageRegionCode: LanguageRegionCode): string {
  return createExploreUrlFromFilters(filters, {
    languageRegionCode,
    ref: 'homepage_explore_showmorecard'
  });
}

const useSections = ({ initialSections, latLng, locationData }: UseSectionsParams) => {
  const languageRegionCode = useLanguageRegionCode();
  const [sections, updateSections] = useState(initialSections);
  const [searchRadiusModifier, updateSearchRadiusModifier] = useState(1);
  const [searchStrategy, setSearchStrategy] = useState<SearchStrategy>('bbox');
  const [lat, lng] = latLng || [0, 0];

  const listItems = useSelector((state: { lists: ListsState }) => state.lists.listItems);

  // Get completed and verified completed trail IDs to exclude from explore-service requests
  const excludedTrailIds = useMemo(() => {
    const completedTrailIds = Object.keys(getItemsInListOfType(listItems, completedListId, 'trail')).map(id => Number(id));
    const verifiedTrailIds = Object.keys(getItemsInListOfType(listItems, verifiedListId, 'trail')).map(id => Number(id));
    return Array.from(new Set([...completedTrailIds, ...verifiedTrailIds]));
  }, [listItems]);

  const isMounted = useRef(true);

  useEffect(() => {
    updateSections(initialSections);
  }, [initialSections]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const getTrailCarouselSections = (): TrailListSection[] => {
    return sections.filter((s): s is TrailListSection => s.section_type === TRAIL_LIST);
  };

  const handleBatchSearchSuccess = (results: ExploreServiceResponse<TrailSearchResult>[], convertedFilters: ExploreServiceFilters[]) => {
    // Cancel any potential long-running effects if the component unmounted.
    if (!isMounted.current) {
      return;
    }

    const isViable = results.some(result => result.searchResults.length >= 8); // any river has at least 8 results
    const hasAnyResults = results.some(result => result.searchResults.length > 0);

    if (!hasAnyResults && locationData?.country && !locationData?.city) {
      // center radius approach
      if (searchStrategy === 'bbox') {
        setSearchStrategy('center-radius');
        return;
      }
      // use country slug for broad search
      if (searchStrategy === 'center-radius') {
        setSearchStrategy('country-slug');
        return;
      }
    }

    // expand bounding box radius if results are insufficient
    if (!isViable && searchRadiusModifier < MAX_RADIUS_MULTIPLIER && searchStrategy === 'bbox') {
      updateSearchRadiusModifier(searchRadiusModifier + 1);
      return;
    }

    const now = new Date();
    const today = [now.getFullYear(), now.getMonth(), now.getDate()].join('-');
    const stickyRandomNumber = seedrandom(today); // same random numbers generated throughout the day
    const getScore = (trail: TrailSearchResult) => {
      return (trail.popularity || 0) + (trail.avg_rating || 0) * 4 + stickyRandomNumber() * RANDOMNESS_FACTOR;
    };

    let resultIndex = 0;
    let hits: TrailSearchResult[];
    const hitIds: number[] = [];
    const resultLimiter = results.length - 1;
    // this callback maps the search results to the rows that have TRAIL_LIST
    // we can only have a top limit of 12 and we cannot have repeat trail cards between sections
    updateSections(prevSections =>
      prevSections
        .map(section => {
          if (section.section_type !== TRAIL_LIST || resultIndex > resultLimiter) {
            return section;
          }

          hits = [];

          const trailsSortedByPopularity = [...results[resultIndex].searchResults].sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
          // Take the top TRAILS_PER_RIVER trails that haven't been displayed yet
          trailsSortedByPopularity.forEach(hit => {
            if (!hitIds.includes(hit.ID) && hits.length < TRAILS_PER_RIVER) {
              hits.push(hit);
              hitIds.push(hit.ID);
            }
          });

          hits.sort((a, b) => {
            return getScore(b) - getScore(a);
          });

          hits.push({
            exploreMoreUrl: createUrlFromFilters(convertedFilters[resultIndex], languageRegionCode)
          } as any);

          resultIndex += 1;
          return { ...section, hits };
        })
        .filter(section => {
          if (section.section_type !== TRAIL_LIST) {
            return true;
          }
          // if the section has less than 3 hits we filter out the section
          return section.hits && section.hits.length > 3;
        })
    );
  };

  const generateTrailListFilters = (legacyFilters: LegacyFilters[]) => {
    const payloads: ExploreServicePayload[] = [];
    const convertedFilters: ExploreServiceFilters[] = [];

    legacyFilters.forEach((legacyFilter, idx) => {
      // Convert legacy filters to explore-service format
      const exploreFilters = convertLegacyFiltersToExploreService(legacyFilter);
      convertedFilters.push(exploreFilters);

      // Convert geo to location with current search strategy
      const location = convertGeoToLocation(legacyFilter.geo, lat, lng, searchRadiusModifier, locationData, searchStrategy);
      // Create payload
      const payload = createExploreServicePayload(exploreFilters, location, legacyFilter.sort, legacyFilter.limit, idx, excludedTrailIds);
      payloads.push(payload);
    });

    // Make batch requests using Promise.all
    Promise.all(
      payloads.map(payload => {
        return post<ExploreServiceResponse<TrailSearchResult>>('/api/alltrails/explore/v1/search', payload);
      })
    )
      .then(results => handleBatchSearchSuccess(results, convertedFilters))
      .catch(error => {
        logError(
          new Error('Failed to fetch trails from explore-service', {
            cause: error
          })
        );
      });
  };

  useEffect(() => {
    // Only fetch trails if latLng is provided
    if (!latLng) {
      return;
    }

    const initialFilters = getTrailCarouselSections()
      .map(list => list.filters)
      .filter((f): f is NonNullable<typeof f> => f !== undefined)
      .map(f => f as LegacyFilters);

    if (initialFilters.length > 0) {
      generateTrailListFilters(initialFilters);
    }
  }, [searchRadiusModifier, lat, lng, latLng, excludedTrailIds, searchStrategy]);

  return sections;
};

export default useSections;
